import {
  AUTH_ANONYMOUS,
  AUTH_LOGGED,
  AUTH_TEAMS,
  CHECK_ACCOUNT_FAILED,
  CHECK_ACCOUNT_SUCCESS,
  LOGIN_SUCCESS,
  LOGOUT,
  MICROSOFT_LOGIN_SUCCESS,
  OAUTH_SUCCESS,
  REFRESH_TOKEN_LOGIN_SUCCESS,
  REQUEST_TOKEN_SUCCESS,
  SET_USER_PERMISSIONS,
  VALIDATE_INVITE_TOKEN_SUCCESS,
} from 'actions';
import update from 'immutability-helper';
import { get } from 'lodash-es';

import createReducer from './createReducer';

export interface User {
  id: string;
  accountId: string;
  firstName: string;
  lastName: string;
  email: string;
  /* eslint-disable camelcase */
  access_token: string | null;
  refresh_token: string | null;
  /* eslint-enable camelcase */
  firebaseToken: string;
  type: 'standard' | 'oauth' | 'teams';
  permissions: string[];
  refreshPatchFilter: boolean;
  authId: string;
  phone: string;
}

interface Teams {
  inTeams: boolean;
  loaded: boolean;
  inTeamsChannelTab: boolean;
  inTeamsPersonalTab: boolean;
  contentUrl: string | null;
  channelId: string | null;
  tabId: string | null;
}

interface Invite {
  email: null | string;
  token: null | string;
  validated: boolean;
}

interface AuthState {
  auth: boolean;
  email: null | string;
  hasAccount: null | boolean;
  invite: Invite;
  organization: null | Record<string, unknown>;
  user: null | User;
  teams: Teams;
}

const INITIAL_STATE: AuthState = {
  auth: false,
  email: null,
  invite: {
    email: null,
    token: null,
    validated: false,
  },
  hasAccount: null,
  organization: null,
  user: null,
  teams: {
    loaded: false,
    inTeams: false,
    inTeamsChannelTab: false,
    inTeamsPersonalTab: false,
    contentUrl: null,
    channelId: null,
    tabId: null,
  },
};

type UserAction = {
  type: string;
  user?: User;
};

type TokenAction = {
  type: string;
  payload?: {
    user?: User;
    organization?: Record<string, unknown>;
  };
};

type ValuesAction = {
  type: string;
  values?: Teams;
};

type PermissionsAction = {
  type: string;
  payload?: string[];
};

export default createReducer(INITIAL_STATE, {
  [AUTH_LOGGED]: (state, action?: UserAction) => {
    const user = action?.user;
    if (user === undefined) {
      return state;
    }
    return { ...state, auth: true, user };
  },
  [AUTH_TEAMS]: (state, action?: ValuesAction) => {
    const values = action?.values;
    if (values === undefined) {
      return state;
    }
    const {
      inTeams,
      inTeamsChannelTab,
      inTeamsPersonalTab,
      contentUrl,
      channelId,
      tabId,
    } = values;
    return update(state, {
      teams: {
        $set: {
          loaded: true,
          inTeams,
          inTeamsChannelTab,
          inTeamsPersonalTab,
          contentUrl,
          channelId,
          tabId,
        },
      },
    });
  },
  [LOGIN_SUCCESS]: (state, action?: UserAction) => {
    const user = action?.user;
    if (user === undefined) {
      return state;
    }
    return { ...state, auth: true, user };
  },
  [MICROSOFT_LOGIN_SUCCESS]: (state) => {
    return {
      ...state,
      auth: true,
    };
  },
  [REFRESH_TOKEN_LOGIN_SUCCESS]: (state, action?: UserAction) => {
    const user = action?.user;
    if (user === undefined) {
      return state;
    }
    const {
      access_token: accessToken,
      permissions,
      refresh_token: refreshToken,
      /*
          This id is placed here for the case of microsoft 365 login
          just after user invite creation.

          The refreshed token is the only place in the flow
          I can see where the FE can get the user id.
        */
      id: userId,
    } = user;
    return update(state, {
      user: {
        $merge: {
          access_token: accessToken,
          refresh_token: refreshToken,
          id: userId,
          permissions,
        },
      },
    });
  },
  [REQUEST_TOKEN_SUCCESS]: (state, action?: TokenAction) => {
    const payload = action?.payload;
    if (payload === undefined) {
      return state;
    }
    const organization = payload.organization || {};
    const user = payload.user || ({} as User);
    return {
      ...state,
      organization,
      user,
    };
  },
  [CHECK_ACCOUNT_SUCCESS]: (state) => {
    if (state.user !== null) {
      return {
        ...state,
        auth: true,
      };
    }
    return state;
  },
  [CHECK_ACCOUNT_FAILED]: (state) => {
    return {
      ...state,
      hasAccount: false,
    };
  },
  [SET_USER_PERMISSIONS]: (state, action?: PermissionsAction) => {
    const payload = action?.payload;
    if (!payload) {
      return state;
    }
    return update(state, {
      user: {
        $merge: {
          permissions: payload,
        },
      },
    });
  },
  [OAUTH_SUCCESS]: (state, action?: UserAction) => {
    const user = action?.user;
    if (user === undefined) {
      return state;
    }
    return { ...state, auth: true, user };
  },
  [AUTH_ANONYMOUS]: (state) => ({ ...state, auth: false }),
  [LOGOUT]: () => ({ ...INITIAL_STATE }),
  [VALIDATE_INVITE_TOKEN_SUCCESS]: (state, payload) => {
    const email = get(payload, 'data.email', null);
    const token = get(payload, 'data.token', null);
    return {
      ...state,
      invite: {
        email,
        token,
        validated: true,
      },
    };
  },
});
